import { useFormItemEnabled } from '@framework/form/item/useFormItemEnabled.ts';
import { Flex } from 'antd';
import clx from 'classnames';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import { type IWidgetAdvancedOption } from '../../../Widget.ts';
import { type IAdvancedSelectColumns } from './IAdvancedSelectColumns.ts';
import { type IRuntimeWidgetAdvancedOption } from './IAdvancedSelectWidget.ts';
import { AdvancedSelectedOptionLabel } from './utils/AdvancedSelectedOptionLabel.tsx';

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  min-height:  40px;
  width: 100%;
  min-width: 0;
  padding: 4px 11px;
  font-size: ${props => props.theme.fontSizeLG}px;
  line-height: ${props => props.theme.lineHeightLG};
  border-width: 1px;
  border-style: solid;
  border-color: ${props => props.theme.colorBorder};
  border-radius: ${props => props.theme.borderRadius}px;
  &.single {
    padding-block: 7px;
  }
  &.multiple {
    padding: 5px;
  }
  &.placeholder {
    color: ${props => props.theme.colorTextPlaceholder};
  }

  // disabilitato
  &.input-disabled,
  &.input-disabled:hover,
  &.input-disabled:focus {
    background-color: ${props => props.theme.colorBgContainerDisabled};  
    cursor: not-allowed;
    color: ${props => props.theme.colorTextDisabled};
  }
  // Errori
  .ant-form-item-has-error & {
    border-color: ${props => props.theme.colorError};
  }

`;

/* Larghezza piena al contenuto, altrimenti il text-align non è percepibile */
const SingleValueContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const MultipleValuesFlex = styled(Flex)`
  flex: 1;
  min-width: 0;
`;

// Padding verticale del tag: rientra nel calc() del pallino qui sotto
const tagPaddingBlock = 2;

const StyledLikeTag = styled.div`
  background: ${props => props.theme.colorBorderSecondary};
  color: ${props => props.theme.colorText};
  border-radius: 4px;
  border: 1px solid ${props => props.theme.colorSplit};
  min-height: 28px;
  /* display block (non flex): i paragrafi occupano tutta la riga e il
     text-align dell'RTE resta visibile; il flusso inline (" - ") è preservato */
  display: block;
  padding: ${tagPaddingBlock}px 6px ${tagPaddingBlock}px 24px;
  margin: 0;
  position: relative;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    /* Pallino centrato sulla prima riga di testo, non sull'intero blocco:
       l'em segue il line box locale (font-size ereditato), il rem no */
    top: calc(
      ${tagPaddingBlock}px + ${props => props.theme.lineHeightLG / 2}em
    );
    left: 8px;

    transform: translateY(-50%);
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${props => props.theme.colorText};
  }
`;

export interface IAdvancedSelectRenderValueProps {
  type: 'single' | 'multiple';
  valueSingle?: IWidgetAdvancedOption['values'] | null;
  valueMultiple?: IRuntimeWidgetAdvancedOption[];
  labelFieldColumns?: string[];
  columns?: IAdvancedSelectColumns[];
  placeholder?: string;

  disabled?: boolean;
}

/**
 * Render dei valori selezionati dal Widget per la selezione singola/multipla avanzata
 */
export function AdvancedSelectRenderValue(
  props: Readonly<IAdvancedSelectRenderValueProps>,
) {
  const {
    type,
    valueSingle,
    valueMultiple,
    labelFieldColumns,
    columns,
    placeholder,
  } = props;

  const { enabled, layoutEnabled } = useFormItemEnabled();
  const disabled = enabled === false || layoutEnabled === false;

  if (
    (type === 'single' && (!valueSingle || isEmpty(valueSingle))) ||
    (type === 'multiple' &&
      (!Array.isArray(valueMultiple) || valueMultiple.length === 0))
  ) {
    return (
      <InputContainer
        className={clx('placeholder', { 'input-disabled': disabled })}
      >
        {placeholder}
      </InputContainer>
    );
  }

  return (
    <InputContainer className={clx(type, { 'input-disabled': disabled })}>
      {type === 'single' ? (
        <SingleValueContainer>
          <AdvancedSelectedOptionLabel
            columns={columns}
            labelFieldColumns={labelFieldColumns}
            selectedValue={valueSingle}
          />
        </SingleValueContainer>
      ) : (
        <MultipleValuesFlex vertical gap={5}>
          {valueMultiple?.map(item => (
            <StyledLikeTag key={item.id}>
              <AdvancedSelectedOptionLabel
                columns={columns}
                labelFieldColumns={labelFieldColumns}
                selectedValue={item.values}
              />
            </StyledLikeTag>
          ))}
        </MultipleValuesFlex>
      )}
    </InputContainer>
  );
}
